<template>
    <div>
        <org-unit-group-list-controls
            v-model="fetchParams"
            :orgUnit="props.orgUnit"
            :teamId="teamId"
            @apply="applyFilters"
            @reset="resetFilters"
            @added="addGroups"
        ></org-unit-group-list-controls>

        <page-loading v-if="loading && !list"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No groups yet.
                <vx-button v-if="isAdminOrOwner" @click="addGroupModal = true" data-test="empty-state-add-button">
                    Add Group
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
                <org-unit-add-groups-modal
                    v-if="isAdminOrOwner"
                    v-model:visible="addGroupModal"
                    :teamId="teamId"
                    :orgUnit="props.orgUnit"
                    @added="addGroups"
                ></org-unit-add-groups-modal>
            </vx-box>
        </template>

        <vx-table v-else-if="list.length" class="xl:grid-cols-5-auto" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>Name</div>
                <div>Status</div>
                <div>External ID</div>
                <div>Users</div>
            </vx-table-row>

            <org-unit-group-row
                v-for="(item, index) in list"
                :modelValue="list[index]"
                :orgUnitId="props.orgUnit.id"
                :key="list[index].group_id"
                @removed="removeFromList($event)"
                @moved="($event) => removeFromList($event.group_id)"
            ></org-unit-group-row>
        </vx-table>

        <div v-if="list.length && fetchParams.cursor" class="mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>
<script setup>
import { orgUnitGroupsClient } from '~/services';
import { ref, watch } from 'vue';
import OrgUnitGroupListControls from './OrgUnitGroupListControls.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { VxBox, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import OrgUnitGroupRow from './OrgUnitGroupRow.vue';
import OrgUnitAddGroupsModal from './modals/OrgUnitAddGroupsModal.vue';

const props = defineProps({
    orgUnit: {
        type: Object,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const addGroupModal = ref(false);

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'name',
    sort_dir: 'desc',
    query: '',
});

const loadData = () => {
    fetchRecords(orgUnitGroupsClient.listOrgUnitGroups(teamId, props.orgUnit.id, { ...fetchParams.value }));
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.group_id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

const addGroups = (groups) => {
    list.value = groups.concat(list.value);
};

watch(
    () => props.orgUnit.id,
    () => {
        resetFilters();
    },
    {
        immediate: true,
    }
);
</script>
